<template>
    <div style="width: 100%" class="py-1">
        <ion-row v-if="entry && entry.item" :class="{ 'promotion-border-top promotion-border-inline' : showPromotionHint }">
            <ion-col
                size="2"
                class="center px-1"
                @click="openReadOnly(entry.item)"
            >
                <div class="icon-container center">
                    <skeleton-base :show="loading" :variants="skeletonVariants">
                        <img
                            v-if="entry.item.imageLink" alt=""
                            :src="(entry.item.imageLink.startsWith('/')) ? `https://app.octopusorder.com${entry.item.imageLink}` : entry.item.imageLink"
                            style="max-height: 100%;"
                        />
                        <ion-icon v-else src="assets/icon/item.svg" class="max-size purple-purple-color"/>
                    </skeleton-base>
                </div>
            </ion-col>

            <ion-col class="center-x flex-d-c" @click="openReadOnly(entry.item)">
                <ion-row>
                    <ion-col class="p-0 fw-b">
                        <skeleton-base :show="loading" :variants="skeletonVariants">
                            {{ entry.item.name }}
                        </skeleton-base>
                    </ion-col>
                </ion-row>

                <!-- <ion-row v-if="entry.isPromotion || promotionAmountStep && entry.quantity >= promotionAmountStep"> example of hint throw pill -->
                <ion-row v-if="entry.isPromotion">
                    <ion-col size="auto" class="pt-1 p-0">
                        <highlight-box :color="HIGHLIGHT_COLOR.CORAL">
                            {{ $t(`enumT.${HIGHLIGHT_MESSAGE.PROMOTION}`) }}
                        </highlight-box>
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col
                        v-if="entry.promotion && entry.promotion.validUntil"
                        size="auto"
                        class="ion-align-items-center pb-0 pt-1 pl-0 d-flex"
                    >
                        <div class="fs-11 pr-1">
                            {{ $t('object.validUntil') }}:
                        </div>

                        <div v-if="!expired" class="fs-11">
                            {{ convertFormat(entry.promotion.validUntil, 'iso', DATE_TIME_FORMAT.DE_SHORT) }}
                        </div>

                        <highlight-box v-else :color="HIGHLIGHT_COLOR.CORAL">
                            {{ $t('object.expired') }}
                        </highlight-box>
                    </ion-col>
                </ion-row>

                <ion-row class="mt-1">
                    <ion-col class="fs-smaller grey1-grey1-color p-0">
                        <skeleton-base :show="loading" :variants="skeletonVariants">
                            {{ entry.item.packaging }}
                        </skeleton-base>
                    </ion-col>
                </ion-row>

                <ion-row class="mt-1">
                    <ion-col class="fs-smaller grey1-grey1-color p-0">
                        <skeleton-base :show="loading" :variants="skeletonVariants">
                            {{ $t('label.articleNo') }}: {{ entry.item.itemNumber }}
                        </skeleton-base>
                    </ion-col>
                </ion-row>
            </ion-col>

            <ion-col v-if="edit" size="auto" class="center-y">
                <button-base
                    :color="ION_VARIANT.GREY_4"
                    class="round-button grey1-grey1-color inherit-color no-shadow"
                    style="width: 50px; height: 50px;"
                    @click="handleEditClick"
                >
                    <ion-icon src="assets/icon/pencil.svg" class="round-button-icon"/>
                </button-base>
            </ion-col>
        </ion-row>

        <ion-row class="stacked-cols" v-if="entry && entry.item" :class="{ 'promotion-border-inline' : showPromotionHint }">
            <ion-col size="2" class="fixed-width-xxs-screen" @click="openReadOnly(entry.item)">
                <div class="fs-smaller grey1-grey1-color center-x">{{ $t('object.amount') }}</div>
                <div class="fw-b center-x">
                    <skeleton-base :show="loading" :variants="skeletonVariants" class="skeleton-fw-b">
                        {{ entry.quantity }}
                    </skeleton-base>
                </div>
            </ion-col>

            <ion-col @click="openReadOnly(entry.item)">
                <template v-if="Number(entry.item.price)">
                    <div class="fs-smaller grey1-grey1-color">{{ $t('object.price') }}</div>
                    <div class="fw-b">
                        <skeleton-base :show="loading" :variants="skeletonVariants" class="skeleton-fw-b">
                            {{ convertNumberWithComma(Number(entry.item.price).toFixed(3)) }} {{ CURRENCY_EUR }}
                        </skeleton-base>
                    </div>
                </template>
            </ion-col>
        </ion-row>

        <ion-row class="stacked-cols" v-if="entry && entry.item && showPromotionHint"
            style="background-color: var(--ion-color-coral); align-items: center; color: white;">
            <ion-col class="fixed-width-xxs-screen" @click="emit('convertToPromotion')">
                <div v-if="promotionAmountStep && entry.quantity < promotionAmountStep" class="fw-b ml-1">
                    <skeleton-base :show="loading" :variants="skeletonVariants" class="skeleton-fw-b">
                        {{ $t('messages.cart.upgradeToPromotion', { amount: promotionAmountStep - entry.quantity, shortTitle: entry.item.promotion?.shortTitle }) }}
                    </skeleton-base>
                </div>
                <div v-else class="fw-b ml-1"> <!-- disabled: ticket #363 → comment #27 -->
                    <skeleton-base :show="loading" :variants="skeletonVariants" class="skeleton-fw-b">
                        {{ $t('messages.cart.convertToPromotion') }}
                    </skeleton-base>
                </div>
            </ion-col>

            <ion-col size="auto" class="py-0">
                <button-base
                    class="round-button inherit-color no-shadow"
                    style="width: 50px; height: 50px;"
                    @click="emit('convertToPromotion');"
                    :color="ION_VARIANT.PINK"
                >
                    <ion-icon src="assets/icon/cart-promo-outline.svg" class="round-button-icon"/>
                </button-base>
            </ion-col>
        </ion-row>
    </div>
</template>

<script setup lang="ts">
import { IonCol, IonIcon, IonRow } from "@ionic/vue";

import ButtonBase from "@/components/button/ButtonBase.vue";
import SkeletonBase from "@/components/various/SkeletonBase.vue";
import HighlightBox from "@/components/card/HighlightBox.vue";

import { openReadOnly } from "@/functions/modal";
import { convertNumberWithComma } from "@/functions/converter";

import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { OrderDetailsEntryProps } from "@/types/props";
import { HIGHLIGHT_COLOR, HIGHLIGHT_MESSAGE } from "@/types/enum/propEnum";
import convertFormat from "../../functions/luxon";
import { DATE_TIME_FORMAT } from "@/types/enum/luxonEnum";
import { DateTime } from "luxon";
import { computed } from "vue";
import { CURRENCY_EUR } from "@/assets/variables";
import { PROMOTION_TYPE } from "@/types/enum/notification";

const props = withDefaults(defineProps<OrderDetailsEntryProps>(), {
    loading: false
})
const emit = defineEmits<{ (e: 'edit'): void; (e: 'convertToPromotion'): void }>()

const handleEditClick = (event: TouchEvent | MouseEvent): void => {
    event.stopPropagation()
    emit('edit')
}

const expired = computed<boolean>(() => {
    if (!props.entry || !props.entry.promotion || !props.entry.promotion.validUntil) return false
    return DateTime.fromISO(props.entry.promotion.validUntil).diffNow('minutes').minutes < 0
})

const showPromotionHint = computed(() => {
    const { entry, promotionAmountStep } = props;
    return !entry?.isPromotion
        && entry?.item.promotion
        && promotionAmountStep
        && entry.quantity < promotionAmountStep                                  // ticket #363 → comment #27
        && entry.item.promotion.promotionType !== PROMOTION_TYPE.BONUS_FLEXIBLE  // ticket #363 → comment #27
})
</script>

<style scoped>
.color-red {
    color: var(--ion-color-danger)
}
.promotion-border-top {
    border-top: 1px solid var(--ion-color-coral);
}
.promotion-border-inline {
    border-inline: 1px solid var(--ion-color-coral);
}

</style>
